import { useQuery } from '@tanstack/react-query'
import { api, fetchClient } from './client'
import type { paths } from './schema'

// The Stock view (UI-SCREENS I-01) and the movement history (I-03), 05b #170. Owner and Staff see
// both, cost figures included (permissions.md Inventory, UI-SCREENS G2).
export type StockPage = paths['/api/w/{slug}/stock']['get']['responses'][200]['content']['application/json']
export type StockRow = StockPage['items'][number]

export type MovementPage = paths['/api/w/{slug}/movements']['get']['responses'][200]['content']['application/json']
export type Movement = MovementPage['items'][number]
export type MovementItem = Movement['stock_item']

// The filters above the movement list; `stockItemId` also drives the history on P-10.
// `locationId` narrows to one Location (Home or one Sales Channel) on top of `locationKind`:
// both apply together (#176 Q2).
export type MovementFilters = {
  stockItemId?: number
  locationKind?: string
  locationId?: number
  sourceType?: string
  from?: string
  to?: string
}

// The kinds of location stock can sit at; one Location at a time is `locationId`, picked from
// the Locations endpoint (api/locations.ts).
export const LOCATION_KINDS = ['materials', 'wip', 'home', 'channel'] as const

// The records that post movements; their labels live in id.json (materials.movements.types).
export const SOURCE_TYPES = ['OpeningBalance', 'Restock', 'Batch', 'Assembly', 'Adjustment', 'Dispatch', 'Return'] as const

export function useStock(
  slug: string,
  { q, page }: { q: string; page: number },
  { enabled = true }: { enabled?: boolean } = {},
) {
  return api.useQuery(
    'get',
    '/api/w/{slug}/stock',
    { params: { path: { slug }, query: { q: q === '' ? undefined : q, page } } },
    { retry: false, enabled },
  )
}

// Every Stock row, all pages walked: for the forms that pick a Variant from stock (Dispatch,
// Return, Adjustment, FG opening balance), which must offer more than the first 50. Keyed under
// the Stock view's key, so the same invalidations refresh it.
export function useAllStock(slug: string, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['get', '/api/w/{slug}/stock', { slug, all: true }],
    enabled,
    retry: false,
    queryFn: async () => {
      const items: StockRow[] = []
      let page: number | null = 1
      while (page !== null) {
        const response: { data?: StockPage; error?: unknown } = await fetchClient.GET('/api/w/{slug}/stock', {
          params: { path: { slug }, query: { page } },
        })
        if (!response.data) throw response.error ?? new Error('stock')
        items.push(...response.data.items)
        page = response.data.next_page ?? null
      }
      return { items }
    },
  })
}

export function useMovements(slug: string, filters: MovementFilters, page: number) {
  return api.useQuery(
    'get',
    '/api/w/{slug}/movements',
    {
      params: {
        path: { slug },
        query: {
          page,
          stock_item_id: filters.stockItemId,
          location_kind: filters.locationKind,
          location_id: filters.locationId,
          source_type: filters.sourceType,
          from: filters.from,
          to: filters.to,
        },
      },
    },
    { retry: false },
  )
}
